import React from 'react';
import {
  AssessmentOutlined,
  DashboardOutlined,
  EmojiEventsOutlined,
  FlashOnOutlined,
  GroupWorkOutlined,
  GroupsOutlined,
  LocalShippingOutlined,
  NotificationsOutlined,
  SettingsOutlined,
  ShieldOutlined,
  StarsOutlined,
  TuneOutlined,
  VolunteerActivismOutlined,
} from '@mui/icons-material';
import { matchPath } from 'react-router-dom';
import { AdminModuleKey, AppRole } from '../types';

export interface AdminRouteMeta {
  path: string;
  title: string;
  breadcrumbLabel?: string;
  description?: string;
  navLabel?: string;
  section?: string;
  moduleKey: AdminModuleKey;
  allowedRoles?: AppRole[];
  icon?: React.ElementType;
  parentPath?: string;
}

export const adminRoutes: AdminRouteMeta[] = [
  {
    path: '/',
    title: 'Overview',
    description: 'Platform pulse, recent activity, and quick actions.',
    navLabel: 'Overview',
    section: 'Workspace',
    moduleKey: 'overview',
    icon: DashboardOutlined,
  },
  {
    path: '/users',
    title: 'User Management',
    breadcrumbLabel: 'Users',
    description: 'Search, filter, and manage user lifecycle and roles.',
    navLabel: 'Users',
    section: 'Workspace',
    moduleKey: 'users',
    icon: GroupsOutlined,
  },
  {
    path: '/circles',
    title: 'Circles',
    breadcrumbLabel: 'Circles',
    description: 'Review, update, and retire community circles and membership activity.',
    navLabel: 'Circles',
    section: 'Workspace',
    moduleKey: 'circles',
    icon: GroupWorkOutlined,
  },
  {
    path: '/circles/:id',
    title: 'Circle Details',
    breadcrumbLabel: 'Details',
    moduleKey: 'circles',
    parentPath: '/circles',
  },
  {
    path: '/challenges',
    title: 'Challenges',
    breadcrumbLabel: 'Challenges',
    description: 'View, create, and manage fundraising challenges.',
    navLabel: 'Challenges',
    section: 'Workspace',
    moduleKey: 'challenges',
    icon: EmojiEventsOutlined,
  },
  {
    path: '/challenges/new',
    title: 'New Challenge',
    breadcrumbLabel: 'Create',
    moduleKey: 'challenges',
    allowedRoles: ['SuperAdmin', 'Content-Manager'],
    parentPath: '/challenges',
  },
  {
    path: '/challenges/:id',
    title: 'Challenge Details',
    breadcrumbLabel: 'Details',
    moduleKey: 'challenges',
    parentPath: '/challenges',
  },
  {
    path: '/challenges/:id/edit',
    title: 'Edit Challenge',
    breadcrumbLabel: 'Edit',
    moduleKey: 'challenges',
    allowedRoles: ['SuperAdmin', 'Content-Manager'],
    parentPath: '/challenges/:id',
  },
  {
    path: '/causes',
    title: 'Causes',
    breadcrumbLabel: 'Causes',
    description: 'Manage cause records, publishing state, and visibility.',
    navLabel: 'Causes',
    section: 'Workspace',
    moduleKey: 'causes',
    icon: VolunteerActivismOutlined,
  },
  {
    path: '/causes/new',
    title: 'Create Cause',
    breadcrumbLabel: 'Create',
    moduleKey: 'causes',
    allowedRoles: ['SuperAdmin', 'Content-Manager'],
    parentPath: '/causes',
  },
  {
    path: '/causes/:id',
    title: 'Cause Details',
    breadcrumbLabel: 'Details',
    moduleKey: 'causes',
    parentPath: '/causes',
  },
  {
    path: '/causes/:id/edit',
    title: 'Edit Cause',
    breadcrumbLabel: 'Edit',
    moduleKey: 'causes',
    allowedRoles: ['SuperAdmin', 'Content-Manager'],
    parentPath: '/causes/:id',
  },
  {
    path: '/content',
    title: 'Content Control',
    breadcrumbLabel: 'Content',
    description: 'Feature visibility, publishing controls, and story content.',
    navLabel: 'Content',
    section: 'Operations',
    moduleKey: 'content',
    icon: TuneOutlined,
  },
  {
    path: '/content/stories',
    title: 'Impact Stories',
    breadcrumbLabel: 'Stories',
    moduleKey: 'content',
    parentPath: '/content',
  },
  {
    path: '/content/stories/new',
    title: 'Create Story',
    breadcrumbLabel: 'Create',
    moduleKey: 'content',
    allowedRoles: ['SuperAdmin', 'Content-Manager'],
    parentPath: '/content/stories',
  },
  {
    path: '/content/stories/:id/edit',
    title: 'Edit Story',
    breadcrumbLabel: 'Edit',
    moduleKey: 'content',
    allowedRoles: ['SuperAdmin', 'Content-Manager'],
    parentPath: '/content/stories',
  },
  {
    path: '/analytics',
    title: 'Analytics & Reports',
    breadcrumbLabel: 'Analytics',
    description: 'Donation reporting, trends, and export-ready tables.',
    navLabel: 'Analytics',
    section: 'Operations',
    moduleKey: 'reporting',
    icon: AssessmentOutlined,
  },
  {
    path: '/donation-tracking',
    title: 'Donation Tracking',
    breadcrumbLabel: 'Tracking',
    description:
      'Simulated donation journey from received to delivered, with routing and beneficiary detail.',
    navLabel: 'Donation Tracking',
    section: 'Operations',
    moduleKey: 'donationTracking',
    icon: LocalShippingOutlined,
  },
  {
    path: '/donation-tracking/:id',
    title: 'Tracking Details',
    breadcrumbLabel: 'Details',
    moduleKey: 'donationTracking',
    parentPath: '/donation-tracking',
  },
  {
    path: '/causes/:causeId/tracking-config',
    title: 'Cause Tracking Config',
    breadcrumbLabel: 'Tracking config',
    moduleKey: 'donationTracking',
    allowedRoles: ['SuperAdmin', 'Content-Manager'],
    parentPath: '/causes/:id',
  },
  {
    path: '/streaks',
    title: 'Kheir Streaks',
    breadcrumbLabel: 'Streaks',
    description: 'Review, approve, and reject user Kheir Streak submissions.',
    navLabel: 'Kheir Streaks',
    section: 'Operations',
    moduleKey: 'streaks',
    icon: FlashOnOutlined,
  },
  {
    path: '/points',
    title: 'Points Console',
    breadcrumbLabel: 'Points',
    description: 'Inspect live point balances, ledger history, and mock donation tools.',
    navLabel: 'Points',
    section: 'Operations',
    moduleKey: 'points',
    icon: StarsOutlined,
  },
  {
    path: '/notifications',
    title: 'Notifications',
    breadcrumbLabel: 'Notifications',
    description: 'Draft, schedule, and monitor push notification broadcasts.',
    navLabel: 'Notifications',
    section: 'Operations',
    moduleKey: 'notifications',
    icon: NotificationsOutlined,
  },
  {
    path: '/notifications/new',
    title: 'New Notification',
    breadcrumbLabel: 'Create',
    moduleKey: 'notifications',
    allowedRoles: ['SuperAdmin', 'Content-Manager'],
    parentPath: '/notifications',
  },
  {
    path: '/notifications/analytics',
    title: 'Notification Analytics',
    breadcrumbLabel: 'Analytics',
    moduleKey: 'notifications',
    parentPath: '/notifications',
  },
  {
    path: '/notifications/:id',
    title: 'Notification Details',
    breadcrumbLabel: 'Details',
    moduleKey: 'notifications',
    parentPath: '/notifications',
  },
  {
    path: '/settings',
    title: 'Settings',
    breadcrumbLabel: 'Settings',
    description: 'Brand, notifications, defaults, and admin preferences.',
    navLabel: 'Settings',
    section: 'Control',
    moduleKey: 'settings',
    icon: SettingsOutlined,
  },
  {
    path: '/roles',
    title: 'Roles & Permissions',
    breadcrumbLabel: 'Roles',
    description: 'Review route protection and module permission coverage.',
    navLabel: 'Roles',
    section: 'Control',
    moduleKey: 'roles',
    icon: ShieldOutlined,
  },
];

export const sidebarSections = Array.from(
  adminRoutes.reduce((map, route) => {
    if (!route.navLabel || !route.section || !route.icon) {
      return map;
    }

    const sectionItems = map.get(route.section) || [];
    sectionItems.push(route);
    map.set(route.section, sectionItems);
    return map;
  }, new Map<string, AdminRouteMeta[]>())
).map(([label, items]) => ({ label, items }));

export const getRouteMeta = (pathname: string) =>
  adminRoutes.find((route) =>
    Boolean(matchPath({ path: route.path, end: true }, pathname))
  );

export const buildBreadcrumbs = (pathname: string) => {
  const crumbs: Array<{ label: string; path?: string }> = [];
  let currentRoute = getRouteMeta(pathname);

  while (currentRoute) {
    const hasPathParams = currentRoute.path.includes(':');
    crumbs.unshift({
      label: currentRoute.breadcrumbLabel || currentRoute.title,
      path: hasPathParams ? undefined : currentRoute.path,
    });

    const parentPath = currentRoute.parentPath;
    currentRoute = parentPath
      ? adminRoutes.find((route) => route.path === parentPath)
      : undefined;
  }

  return crumbs;
};
